/**
 * Backroom v2/v2.3 operator projection.
 * Teleporte owns coordination semantics. JARVIS renders them and may add local observations,
 * but never manufactures authoritative ACK, conflict, ownership, escalation, or freshness state.
 */
import {
  classifyPresence,
  canonicalAgentId,
  snippet,
} from '../components/backroomLoadPolicy.mjs';

export const BACKROOM_V2_EVENT_KINDS = [
  'PROPOSAL','ACK','NACK','DECIDE','ORDERS','STATUS','BLOCKER','HANDOFF','VERIFY','CONFLICT'
];

const GHOST_CANONICAL = new Set(['X']);
const REQUIRED_JOINERS = ['ChatGPT'];
const COCKPIT_AGENT = 'EXTENSUS';

export function backroomPrincipalLabel(event) {
  return event?.principal || 'unknown-principal';
}

export function backroomPresenceState(lastSeenAt, now = Date.now()) {
  return classifyPresence(lastSeenAt, now);
}

export function backroomProofLabel(attestation) {
  const status = String(attestation?.status || 'not_executed').toLowerCase().replace(/\s+/g, '_');
  if (!status || status === 'pending' || status === 'not_executed') return 'NOT EXECUTED';
  if (status === 'failed' || status === 'fail' || status === 'error') return 'FAILED';
  return String(attestation?.status || status).toUpperCase();
}

/** Legacy-only helper retained for archived pre-v2 rows. Never used to classify typed input. */
export function extractKindFromContent(content) {
  const m = String(content || '').match(/^\[([A-Z_]+)\]/i);
  return m ? m[1].toUpperCase() : null;
}

function isTypedBackroom(raw = {}) {
  const version = String(raw.schema_version || '');
  return raw.protocol_id === 'teleporte.backroom.coordination' || version.startsWith('2.2') || version.startsWith('2.3');
}

export function normalizeBackroomV2Event(raw = {}) {
  const typed = isTypedBackroom(raw);
  const schemaVersion = String(raw.schema_version || '');
  const schemaMajor = Number.parseInt(schemaVersion.split('.')[0] || '0', 10);
  let kind = null;
  let principal = null;
  let reason = null;
  if (typed) {
    kind = raw.kind == null ? null : String(raw.kind).trim().toUpperCase();
    principal = raw.principal || null;
    if (schemaMajor > 2) reason = 'UNSUPPORTED_PROTOCOL_VERSION';
    else if (!kind) reason = 'MISSING_TYPED_KIND';
    else if (!BACKROOM_V2_EVENT_KINDS.includes(kind)) reason = 'UNSUPPORTED_EVENT_KIND';
    else if (!principal) reason = 'MISSING_TYPED_PRINCIPAL';
  } else {
    kind = String(raw.kind || raw.stage || extractKindFromContent(raw.content || raw.text) || 'STATUS').trim().toUpperCase();
    principal = raw.principal || raw.authenticated_principal || raw.source_chat || raw.agent || 'legacy-unknown';
    if (!BACKROOM_V2_EVENT_KINDS.includes(kind)) reason = 'LEGACY_UNSUPPORTED_EVENT_KIND';
  }
  return {
    message_id: raw.message_id || raw.id || null,
    thread_seq: Number(raw.thread_seq || 0),
    thread_id: raw.thread_id || raw.thread || null,
    protocol_id: raw.protocol_id || null,
    schema_version: raw.schema_version || null,
    principal,
    kind,
    task_id: raw.task_id || null,
    correlation_id: raw.correlation_id || null,
    reply_to: raw.reply_to || null,
    root_message_id: raw.root_message_id || null,
    causal_depth: Number(raw.causal_depth || 0),
    requires_ack: Boolean(raw.requires_ack),
    ack_policy: raw.ack_policy || null,
    observed_canonical_revision: raw.observed_canonical_revision ?? (typed ? null : raw.canonical_revision_ref ?? null),
    accepted_canonical_revision: raw.accepted_canonical_revision ?? null,
    payload_hash: raw.payload_hash || null,
    request_hash: raw.request_hash || null,
    created_at: raw.created_at || raw.timestamp || null,
    content: raw.content || raw.text || '',
    evidence: Array.isArray(raw.evidence) ? raw.evidence : [],
    unsupported: Boolean(reason),
    reason,
    typed,
  };
}

export function buildOperatorDecisionPackets(summary = {}) {
  const reasons = Array.isArray(summary.operator_required) ? summary.operator_required : [];
  return reasons.map((reason) => ({
    reason,
    task_id: summary.task_id || null,
    active_leases: summary.active_leases || [],
    active_tasks: summary.active_tasks || [],
    conflicts: summary.conflicts || [],
    blockers: summary.blockers || [],
    pending_acks: summary.pending_acks || [],
    rejected_acks: summary.rejected_acks || [],
    overdue_acks: summary.overdue_acks || [],
    latest_decisions: summary.latest_decisions || [],
    canonical_revision_ref: summary.canonical_revision_ref ?? summary.canonical_revision_snapshot ?? null,
    through_seq: summary.through_seq ?? null,
    latest_seq: summary.latest_seq ?? null,
    stale_by: summary.stale_by ?? null,
    projection_state: summary.projection_state ?? null,
    summary_hash: summary.summary_hash ?? summary.coordination_hash ?? null,
    detail: summary.details?.[reason] || null,
  }));
}

function newestPresence(rows) {
  const list = Array.isArray(rows) ? [...rows] : [];
  list.sort((a, b) => (Date.parse(String(b?.last_seen_at || '').replace(' ', 'T')) || 0) - (Date.parse(String(a?.last_seen_at || '').replace(' ', 'T')) || 0));
  return list[0] || null;
}

export function collapsePresence(presence, now = Date.now()) {
  const byCanon = new Map();
  for (const row of presence || []) {
    const rawIdentity = row?.principal || row?.agent;
    const canon = canonicalAgentId(rawIdentity);
    if (!canon || GHOST_CANONICAL.has(canon)) continue;
    const prev = byCanon.get(canon);
    const incomingTs = Date.parse(String(row.last_seen_at || '').replace(' ', 'T')) || 0;
    const prevTs = prev ? Date.parse(String(prev.last_seen_at || '').replace(' ', 'T')) || 0 : -1;
    if (!prev || incomingTs >= prevTs) {
      byCanon.set(canon, {
        ...row,
        principal: row.principal || rawIdentity,
        agent: canon,
        state: backroomPresenceState(row.last_seen_at, now),
      });
    }
  }
  return [...byCanon.values()];
}

function latestByKind(events, kind) {
  const target = String(kind || '').toUpperCase();
  for (let i = events.length - 1; i >= 0; i -= 1) {
    if (!events[i]?.unsupported && events[i]?.kind === target) return events[i];
  }
  return null;
}

function chipLabel({ id, state, claim, present, joiner }) {
  const owner = claim?.owner_principal || claim?.agent || '';
  const foreign = owner && owner.toUpperCase() !== String(id || '').toUpperCase();
  if (claim && state === 'LIVE') return foreign ? `LEASED · ${owner}` : 'LEASED';
  if (claim) return foreign ? `LEASED · ${state} · ${owner}` : `LEASED · ${state}`;
  if (state === 'LIVE') return present?.status || 'here';
  if (present) return state;
  if (joiner) return 'no checkin';
  return 'idle';
}

export function projectBackroomV2({ messages = [], board = {}, summary = {}, summaryReceipt = null, canonicalObservation = null, now = Date.now(), proofs: proofInput } = {}) {
  const rows = Array.isArray(messages) ? messages : [];
  const events = rows.map(normalizeBackroomV2Event);
  const validEvents = events.filter((event) => !event.unsupported);
  const collapsed = collapsePresence(board?.presence, now);
  const live = collapsed.filter((p) => p.state === 'LIVE');
  const leases = Array.isArray(summary?.active_leases)
    ? summary.active_leases
    : Array.isArray(summary?.active_tasks) ? summary.active_tasks : [];
  const pendingAckIds = Array.isArray(summary?.pending_acks) ? summary.pending_acks : [];
  const blockers = Array.isArray(summary?.blockers) ? summary.blockers : validEvents.filter((event) => event.kind === 'BLOCKER').slice(-3);
  const latestDecide = latestByKind(validEvents, 'DECIDE');
  const latestOrders = latestByKind(validEvents, 'ORDERS');
  const latestHandoff = latestByKind(validEvents, 'HANDOFF');
  const chatgpt = collapsed.find((p) => p.agent === 'ChatGPT') || null;
  const missingCheckins = REQUIRED_JOINERS.filter((id) => !collapsed.some((p) => p.agent === id));
  const staleWorking = collapsed.filter((p) => ['working','online'].includes(String(p.status || '').toLowerCase()) && p.state === 'STALE');

  const roster = [
    ...(board?.roster || []),
    { id: COCKPIT_AGENT, role: 'cockpit' },
    ...REQUIRED_JOINERS.map((id) => ({ id, role: 'joiner' })),
  ];
  const seenIds = new Set();
  const rosterChips = [];
  for (const r of roster) {
    const id = String(r.id || '').trim();
    if (!id || seenIds.has(id.toUpperCase())) continue;
    seenIds.add(id.toUpperCase());
    const present = collapsed.find((p) => p.agent.toUpperCase() === id.toUpperCase()) || null;
    const claim = leases.find((c) => String(c.owner_principal || c.agent || '').toUpperCase() === id.toUpperCase()) || null;
    const state = present ? present.state : 'OFFLINE';
    rosterChips.push({ id, role:r.role || '', present, claim, state, label:chipLabel({id,state,claim,present,joiner:r.role === 'joiner'}) });
  }

  const operatorRequired = Array.isArray(summary?.operator_required) ? [...new Set(summary.operator_required)] : [];
  const localObservations = [];
  if (missingCheckins.length) localObservations.push({ reason:'missing_checkin', detail:missingCheckins.join(', ') });
  if (staleWorking.length) localObservations.push({ reason:'stale_working', detail:staleWorking.map((p)=>`${p.agent} ${p.state}`).join(' · ') });
  if (events.some((event) => event.unsupported)) localObservations.push({ reason:'invalid_typed_envelope', detail:events.filter((e)=>e.unsupported).map((e)=>`${e.message_id || '?'}:${e.reason}`).join(' · ') });

  const authoritativeSummary = {
    ...summary,
    summary_hash: summaryReceipt?.coordination_hash || summary?.summary_hash || null,
  };
  const packets = buildOperatorDecisionPackets(authoritativeSummary);
  const newest = validEvents.at(-1) || null;
  const proofs = Array.isArray(proofInput) && proofInput.length ? proofInput : [
    {kind:'runtime_probe',status:rows.length || board?.ok || board?.roster ? 'passed' : 'not_executed'},
    {kind:'typed_protocol_fields',status:events.length && events.every((e)=>e.typed && !e.unsupported) ? 'passed' : 'not_executed'},
    {kind:'chatgpt_checkin',status:chatgpt ? 'passed' : 'not_executed'},
    {kind:'local_test',status:'not_executed'},
    {kind:'production_deploy',status:'not_executed'},
  ];

  return {
    schema: 'jarvis.backroom-v2.projection',
    authority: 'projection-only',
    canonical: 'teleporte-d1',
    live,
    owns: leases,
    awaits_ack: pendingAckIds,
    blocked: blockers,
    decide: latestDecide,
    orders: latestOrders,
    handoff: latestHandoff,
    missing_checkins: missingCheckins,
    stale_working: staleWorking,
    local_observations: localObservations,
    operator_required: operatorRequired,
    roster_chips: rosterChips,
    packets,
    canonical_revision: canonicalObservation?.revision ?? summary?.canonical_revision_ref ?? summary?.canonical_revision_snapshot ?? null,
    canonical_observation: canonicalObservation || null,
    summary_receipt: summaryReceipt || null,
    proofs: proofs.map((p)=>({...p,label:backroomProofLabel(p)})),
    events,
    freshness: {
      newest_id: newest?.message_id ?? null,
      newest_at: newest?.created_at ?? null,
      board_observed_at: board?.observed_at ?? null,
      through_seq: summary?.through_seq ?? null,
      latest_seq: summary?.latest_seq ?? null,
      stale_by: summary?.stale_by ?? null,
      projection_state: summary?.projection_state ?? null,
      live_count: live.length,
      stale_presence_count: rosterChips.filter((c)=>c.state === 'STALE').length,
      offline_count: rosterChips.filter((c)=>c.state === 'OFFLINE').length,
    },
    snippets: {
      live: live.length ? live.map((p)=>`${p.agent}${p.status ? ` (${p.status})` : ''}`).join(' · ') : 'nobody in the last 15 minutes',
      owns: leases.length ? leases.map((c)=>`${c.owner_principal || c.agent || '?'}: ${snippet((Array.isArray(c.paths) ? c.paths.join(', ') : '') || c.branch,80)}`).join(' · ') : 'no active leases',
      awaits: pendingAckIds.length ? pendingAckIds.map((id)=>`#${id}`).join(' · ') : latestHandoff ? `latest HANDOFF #${latestHandoff.message_id} from ${latestHandoff.principal} — ${snippet(latestHandoff.content,100)}` : 'nothing open',
      blocked: blockers.length ? blockers.map((b)=>snippet(b.content,90)).join(' · ') : 'none',
    },
  };
}

export function newestPresenceRow(presence, agentId) {
  const id = canonicalAgentId(agentId).toUpperCase();
  return newestPresence((presence || []).filter((p) => canonicalAgentId(p.principal || p.agent).toUpperCase() === id));
}

const backroomV2 = {
  normalizeBackroomV2Event,
  projectBackroomV2,
};
export default backroomV2;
